import React, { useState, useEffect, useCallback } from 'react';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { useModerator } from '../hooks/useModerator';
import { useNavigate, Link } from 'react-router-dom';
import { ProtectedRoute } from '../auth/ProtectedRoute';
import { Button } from '../components/common/Button';
import { Modal } from '../components/common/Modal';
import { Textarea } from '../components/common/Textarea';
import { moderationApi } from '../api/moderation';
import type { Comment } from '../types/comment';

const PAGE_SIZE = 50;

const FlaggedCommentsPageContent: React.FC = () => {
  const isModerator = useModerator();
  const navigate = useNavigate();
  const [page] = useState(0);
  const [flagged, setFlagged] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approveCommentId, setApproveCommentId] = useState<string | null>(null);
  const [rejectCommentId, setRejectCommentId] = useState<string | null>(null);
  const [approveReason, setApproveReason] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);
  const [rejectError, setRejectError] = useState<string | null>(null);

  const fetchFlagged = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await moderationApi.getFlaggedComments(page, PAGE_SIZE);
      setFlagged(res.content);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch flagged comments');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    if (!isModerator) {
      navigate('/');
      return;
    }
    fetchFlagged();
  }, [fetchFlagged, isModerator, navigate]);

  const handleApprove = (commentId: string) => {
    setApproveCommentId(commentId);
    setApproveReason('');
    setApproveError(null);
  };

  const confirmApprove = async () => {
    if (!approveCommentId) return;
    setApproving(true);
    setApproveError(null);
    try {
      await moderationApi.approveCommentFlag(approveCommentId, { reason: approveReason.trim() || undefined });
      setApproveCommentId(null);
      setApproveReason('');
      await fetchFlagged();
    } catch (err) {
      setApproveError(err instanceof Error ? err.message : 'Failed to approve flag');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = (commentId: string) => {
    setRejectCommentId(commentId);
    setRejectReason('');
    setRejectError(null);
  };

  const confirmReject = async () => {
    if (!rejectCommentId) return;
    setRejecting(true);
    setRejectError(null);
    try {
      await moderationApi.rejectCommentFlag(rejectCommentId, { reason: rejectReason.trim() || undefined });
      setRejectCommentId(null);
      setRejectReason('');
      await fetchFlagged();
    } catch (err) {
      setRejectError(err instanceof Error ? err.message : 'Failed to reject flag');
    } finally {
      setRejecting(false);
    }
  };

  const truncateText = (text: string, maxLength: number = 150): string => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const formatDate = (iso?: string) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  if (!isModerator) return null;

  if (loading && flagged.length === 0) {
    return <LoadingSpinner size="large" />;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  return (
    <div className="deleted-posts-page">
      <div className="deleted-posts-page-header">
        <h1>Flagged Comments</h1>
        <p className="deleted-posts-description">
          Comments reported as inappropriate. Approve (uphold) or reject (dismiss) each flag. Only visible to moderators.
        </p>
      </div>

      {flagged.length === 0 ? (
        <div className="deleted-posts-empty">
          <p>No flagged comments.</p>
        </div>
      ) : (
        <div className="deleted-posts-table-container">
          <table className="deleted-posts-table">
            <thead>
              <tr>
                <th>Date flagged</th>
                <th>Reason</th>
                <th>Comment content</th>
                <th>Author</th>
                <th>Post</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {flagged.map((comment) => (
                <tr key={comment.id}>
                  <td className="deletion-reason-cell" data-label="Date flagged">{formatDate(comment.flaggedAt)}</td>
                  <td className="deletion-reason-cell" data-label="Reason">
                    {comment.flagReason || <em>No reason provided</em>}
                  </td>
                  <td className="post-content-cell" data-label="Comment">
                    {truncateText(comment.content, 150)}
                  </td>
                  <td data-label="Author">{comment.authorUsername ?? comment.authorId}</td>
                  <td data-label="Post">
                    <Link to={`/posts/${comment.postId}`} className="post-link">
                      View post
                    </Link>
                  </td>
                  <td className="actions-cell" data-label="Actions">
                    <div className="deleted-posts-actions">
                      <Button variant="success" size="small" onClick={() => handleApprove(comment.id)}>
                        Approve
                      </Button>
                      <Button variant="danger" size="small" onClick={() => handleReject(comment.id)}>
                        Reject
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={approveCommentId !== null}
        onClose={() => {
          setApproveCommentId(null);
          setApproveReason('');
          setApproveError(null);
        }}
        title="Approve flag"
      >
        <div className="restore-modal">
          <p>Uphold the flag? The comment will be hidden (soft-deleted).</p>
          <Textarea
            label="Reason (optional)"
            value={approveReason}
            onChange={(e) => setApproveReason(e.target.value)}
            placeholder="Reason for approval..."
            rows={4}
          />
          {approveError && <div className="error-message">{approveError}</div>}
          <div className="modal-actions">
            <Button variant="primary" onClick={confirmApprove} disabled={approving}>
              {approving ? 'Approving…' : 'Approve'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setApproveCommentId(null);
                setApproveReason('');
                setApproveError(null);
              }}
              disabled={approving}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={rejectCommentId !== null}
        onClose={() => {
          setRejectCommentId(null);
          setRejectReason('');
          setRejectError(null);
        }}
        title="Reject flag"
      >
        <div className="restore-modal">
          <p>Dismiss the flag? The comment will become visible again.</p>
          <Textarea
            label="Reason (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Reason for rejection..."
            rows={4}
          />
          {rejectError && <div className="error-message">{rejectError}</div>}
          <div className="modal-actions">
            <Button variant="primary" onClick={confirmReject} disabled={rejecting}>
              {rejecting ? 'Rejecting…' : 'Reject'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setRejectCommentId(null);
                setRejectReason('');
                setRejectError(null);
              }}
              disabled={rejecting}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export const FlaggedCommentsPage: React.FC = () => {
  return (
    <ProtectedRoute>
      <FlaggedCommentsPageContent />
    </ProtectedRoute>
  );
};
